import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FormEvent,
  type ReactNode
} from "react";
import type {
  Account,
  Budget,
  Card,
  Category,
  DashboardData,
  ImportPreview,
  Transaction,
  TransactionKind,
  TransactionStatus
} from "@financas/contracts";
import {
  api,
  ApiError,
  authenticateTelegram,
  exchangeLoginLink,
  getSessionToken,
  setSessionToken
} from "./api";
import { parseNubankCsv, type CsvParseResult } from "./csv-parser";

declare global {
  interface Window {
    Telegram?: {
      WebApp?: {
        initData: string;
        ready(): void;
        expand(): void;
        close(): void;
      };
    };
  }
}

interface ReferenceData {
  categories: Category[];
  accounts: Account[];
  cards: Card[];
  devices: Array<{ id: string; name: string; sourcePackage: string; lastSeenAt: string | null; revoked: boolean }>;
}

type AuthState = "loading" | "authenticated" | "guest" | "error";

const BOT_USERNAME = (import.meta.env.VITE_BOT_USERNAME as string | undefined) ?? "";
let authenticationBootstrap: Promise<"authenticated" | "guest"> | null = null;

function brl(amountMinor: number): string {
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(amountMinor / 100);
}

function currentMonth(): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "America/Sao_Paulo", year: "numeric", month: "2-digit" })
    .format(new Date());
}

function routeFromHash(): { path: string; params: URLSearchParams } {
  const hash = window.location.hash.replace(/^#/, "") || "/";
  const [path, query = ""] = hash.split("?");
  return { path: path || "/", params: new URLSearchParams(query) };
}

async function bootstrapAuthentication(): Promise<"authenticated" | "guest"> {
  const current = routeFromHash();
  if (current.path === "/login") {
    const token = current.params.get("token");
    if (!token) throw new Error("Link de acesso incompleto.");
    await exchangeLoginLink(token);
    const next = current.params.get("next") || "/";
    window.location.hash = next.startsWith("/") ? next : "/";
    return "authenticated";
  }
  if (window.Telegram?.WebApp?.initData) {
    window.Telegram.WebApp.ready();
    window.Telegram.WebApp.expand();
    await authenticateTelegram(window.Telegram.WebApp.initData);
    return "authenticated";
  }
  if (getSessionToken()) {
    await api("/auth/me");
    return "authenticated";
  }
  return "guest";
}

function moneyInputToMinor(value: string): number {
  const normalized = value.includes(",") ? value.replaceAll(".", "").replace(",", ".") : value;
  return Math.round(Number(normalized) * 100);
}

function Button({ children, variant = "primary", ...props }: React.ButtonHTMLAttributes<HTMLButtonElement> & { variant?: "primary" | "quiet" | "danger" }): ReactNode {
  return <button className={`button button--${variant}`} {...props}>{children}</button>;
}

function Empty({ children }: { children: ReactNode }): ReactNode {
  return <div className="empty"><span className="empty__dot" />{children}</div>;
}

function Progress({ value, color = "#c7f34a" }: { value: number; color?: string }): ReactNode {
  return <div className="progress"><span style={{ width: `${Math.min(100, Math.max(0, value))}%`, background: color }} /></div>;
}

export function App(): ReactNode {
  const [auth, setAuth] = useState<AuthState>("loading");
  const [authMessage, setAuthMessage] = useState("");
  const [route, setRoute] = useState(routeFromHash);
  const [month, setMonth] = useState(currentMonth);
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [reference, setReference] = useState<ReferenceData | null>(null);
  const [budgets, setBudgets] = useState<Budget[]>([]);
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [editing, setEditing] = useState<Transaction | null>(null);

  useEffect(() => {
    const onHash = (): void => setRoute(routeFromHash());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  useEffect(() => {
    const bootstrap = async (): Promise<void> => {
      try {
        authenticationBootstrap ??= bootstrapAuthentication();
        setAuth(await authenticationBootstrap);
      } catch (reason) {
        setSessionToken(null);
        setAuthMessage(reason instanceof Error ? reason.message : "Não foi possível entrar.");
        setAuth("error");
      }
    };
    void bootstrap();
  }, []);

  const loadData = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError("");
    try {
      const [dashboardData, transactionData, referenceData, budgetData] = await Promise.all([
        api<DashboardData>(`/dashboard?month=${month}`),
        api<Transaction[]>(`/transactions?month=${month}&limit=200`),
        api<ReferenceData>("/reference-data"),
        api<Budget[]>(`/budgets?month=${month}`)
      ]);
      setDashboard(dashboardData);
      setTransactions(transactionData);
      setReference(referenceData);
      setBudgets(budgetData);
      const editId = routeFromHash().params.get("edit");
      if (editId) setEditing(transactionData.find((item) => item.id === editId) ?? null);
    } catch (reason) {
      if (reason instanceof ApiError && reason.status === 401) setAuth("guest");
      else setError(reason instanceof Error ? reason.message : "Falha ao carregar seus dados.");
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => {
    if (auth === "authenticated") void loadData();
  }, [auth, loadData]);

  const showNotice = (message: string): void => {
    setNotice(message);
    window.setTimeout(() => setNotice(""), 3500);
  };

  if (auth === "loading") return <Splash label="Abrindo seu painel…" />;
  if (auth === "guest" || auth === "error") {
    return (
      <main className="auth-shell">
        <div className="auth-card">
          <div className="brand-mark">$</div>
          <p className="eyebrow">ACESSO PRIVADO</p>
          <h1>Seu dinheiro em um lugar só.</h1>
          <p>Abra este painel pelo comando <strong>/painel</strong> no seu bot do Telegram. O link é pessoal e expira em dez minutos.</p>
          {authMessage && <p className="error-box">{authMessage}</p>}
          {BOT_USERNAME && <a className="button button--primary link-button" href={`https://t.me/${BOT_USERNAME}`}>Abrir o bot</a>}
        </div>
      </main>
    );
  }

  const navigation = [
    ["/", "Visão geral"],
    ["/transactions", "Lançamentos"],
    ["/budgets", "Orçamentos"],
    ["/structure", "Contas e cartões"],
    ["/import", "Importar"],
    ["/settings", "Configurações"]
  ];

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <a className="brand" href="#/"><span className="brand-mark brand-mark--small">$</span><span>Meu Dinheiro</span></a>
        <nav>
          {navigation.map(([path, label]) => (
            <a key={path} className={route.path === path ? "active" : ""} href={`#${path}`}>{label}</a>
          ))}
        </nav>
        <div className="sidebar__footer"><span className="status-dot" /> Dados sincronizados</div>
      </aside>
      <main className="content">
        <header className="topbar">
          <div>
            <p className="eyebrow">FINANÇAS PESSOAIS</p>
            <h1>{navigation.find(([path]) => path === route.path)?.[1] ?? "Meu Dinheiro"}</h1>
          </div>
          <label className="month-picker">Mês<input type="month" value={month} onChange={(event) => setMonth(event.target.value)} /></label>
        </header>
        {notice && <div className="toast">{notice}</div>}
        {error && <div className="error-box">{error}</div>}
        {loading && !dashboard ? <Splash label="Carregando números…" compact /> : (
          <>
            {route.path === "/" && dashboard && <Dashboard dashboard={dashboard} onEdit={setEditing} />}
            {route.path === "/transactions" && <Transactions transactions={transactions} onEdit={setEditing} />}
            {route.path === "/budgets" && reference && (
              <Budgets budgets={budgets} categories={reference.categories} month={month} onSaved={async () => { await loadData(); showNotice("Orçamento salvo."); }} />
            )}
            {route.path === "/structure" && reference && (
              <Structure reference={reference} onSaved={async () => { await loadData(); showNotice("Configuração salva."); }} />
            )}
            {route.path === "/import" && <ImportPage onCommitted={async () => { await loadData(); showNotice("Importação concluída."); }} />}
            {route.path === "/settings" && reference && (
              <Settings reference={reference} onChanged={loadData} onLogout={() => { setSessionToken(null); setAuth("guest"); }} />
            )}
          </>
        )}
      </main>
      {editing && reference && (
        <TransactionEditor
          transaction={editing}
          reference={reference}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await loadData(); showNotice("Lançamento atualizado."); }}
        />
      )}
    </div>
  );
}

function Splash({ label, compact = false }: { label: string; compact?: boolean }): ReactNode {
  return <div className={compact ? "splash splash--compact" : "splash"}><div className="spinner" /><p>{label}</p></div>;
}

function Dashboard({ dashboard, onEdit }: { dashboard: DashboardData; onEdit(transaction: Transaction): void }): ReactNode {
  const maxCategory = Math.max(1, ...dashboard.byCategory.map((item) => item.amountMinor));
  return (
    <div className="page-grid">
      <section className="kpis">
        <Kpi label="Entradas" value={brl(dashboard.incomeMinor)} tone="green" />
        <Kpi label="Gastos" value={brl(dashboard.expenseMinor)} tone="dark" />
        <Kpi label="Sobra do mês" value={brl(dashboard.balanceMinor)} tone={dashboard.balanceMinor >= 0 ? "lime" : "red"} />
        <Kpi label="Para revisar" value={String(dashboard.pendingCount)} tone="sand" />
      </section>
      <section className="panel panel--wide">
        <div className="panel__header"><div><p className="eyebrow">DISTRIBUIÇÃO</p><h2>Gastos por categoria</h2></div><strong>{brl(dashboard.expenseMinor - dashboard.refundMinor)}</strong></div>
        {dashboard.byCategory.length === 0 ? <Empty>Confirme alguns gastos para ver a distribuição.</Empty> : (
          <div className="category-bars">
            {dashboard.byCategory.map((item) => (
              <div className="category-row" key={item.categoryId ?? "none"}>
                <span>{item.name}</span><Progress value={(item.amountMinor / maxCategory) * 100} /><strong>{brl(item.amountMinor)}</strong>
              </div>
            ))}
          </div>
        )}
      </section>
      <section className="panel">
        <div className="panel__header"><div><p className="eyebrow">ATENÇÃO</p><h2>Orçamentos</h2></div><a href="#/budgets">Ajustar</a></div>
        {dashboard.budgetAlerts.length === 0 ? <Empty>Nenhum limite em alerta.</Empty> : dashboard.budgetAlerts.map((budget) => (
          <div className="budget-mini" key={budget.id}><div><strong>{budget.categoryName}</strong><span>{budget.percentage}%</span></div><Progress value={budget.percentage} color={budget.percentage >= 100 ? "#ef5b5b" : "#f6c945"} /></div>
        ))}
      </section>
      <section className="panel">
        <div className="panel__header"><div><p className="eyebrow">PRÓXIMAS</p><h2>Parcelas</h2></div></div>
        {dashboard.upcomingInstallments.length === 0 ? <Empty>Nenhuma parcela futura.</Empty> : dashboard.upcomingInstallments.slice(0, 5).map((item) => (
          <div className="list-line" key={item.id}><span>{item.merchant ?? "Compra parcelada"}<small>{item.sequence}/{item.total} · {item.dueDate}</small></span><strong>{brl(item.amountMinor)}</strong></div>
        ))}
      </section>
      <section className="panel panel--full">
        <div className="panel__header"><div><p className="eyebrow">ÚLTIMOS</p><h2>Lançamentos recentes</h2></div><a href="#/transactions">Ver todos</a></div>
        <TransactionList transactions={dashboard.recent} onEdit={onEdit} />
      </section>
    </div>
  );
}

function Kpi({ label, value, tone }: { label: string; value: string; tone: string }): ReactNode {
  return <article className={`kpi kpi--${tone}`}><span>{label}</span><strong>{value}</strong></article>;
}

function TransactionList({ transactions, onEdit }: { transactions: Transaction[]; onEdit(transaction: Transaction): void }): ReactNode {
  const labels: Record<TransactionKind, string> = { income: "Entrada", expense: "Gasto", transfer: "Transferência", refund: "Estorno" };
  if (transactions.length === 0) return <Empty>Nenhum lançamento neste período.</Empty>;
  return <div className="transaction-list">{transactions.map((transaction) => (
    <button type="button" className="transaction-row" key={transaction.id} onClick={() => onEdit(transaction)}>
      <span className={`transaction-icon transaction-icon--${transaction.kind}`}>{transaction.kind === "income" ? "+" : transaction.kind === "expense" ? "−" : "↔"}</span>
      <span className="transaction-main"><strong>{transaction.merchant ?? transaction.description ?? labels[transaction.kind]}</strong><small>{transaction.economicDate} · {transaction.categoryName ?? "Sem categoria"}</small></span>
      <span className="transaction-value"><strong>{transaction.kind === "expense" ? "−" : transaction.kind === "income" || transaction.kind === "refund" ? "+" : ""}{brl(transaction.amountMinor)}</strong><small className={`badge badge--${transaction.status}`}>{transaction.status}</small></span>
    </button>
  ))}</div>;
}

function Transactions({ transactions, onEdit }: { transactions: Transaction[]; onEdit(transaction: Transaction): void }): ReactNode {
  const [filter, setFilter] = useState("all");
  const filtered = filter === "all" ? transactions : transactions.filter((transaction) => transaction.status === filter);
  return <section className="panel panel--full"><div className="panel__header"><div><p className="eyebrow">HISTÓRICO</p><h2>{transactions.length} lançamentos</h2></div><select value={filter} onChange={(event) => setFilter(event.target.value)}><option value="all">Todos</option><option value="pending">Pendentes</option><option value="confirmed">Confirmados</option><option value="ignored">Ignorados</option></select></div><TransactionList transactions={filtered} onEdit={onEdit} /></section>;
}

function Budgets({ budgets, categories, month, onSaved }: { budgets: Budget[]; categories: Category[]; month: string; onSaved(): Promise<void> }): ReactNode {
  const [categoryId, setCategoryId] = useState(categories[0]?.id ?? "");
  const [limit, setLimit] = useState("");
  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    const limitMinor = moneyInputToMinor(limit);
    if (!categoryId || !Number.isInteger(limitMinor) || limitMinor <= 0) return;
    await api("/budgets", { method: "POST", body: JSON.stringify({ categoryId, month, limitMinor }) });
    setLimit("");
    await onSaved();
  };
  return <div className="split-layout"><section className="panel"><p className="eyebrow">LIMITES DE {month}</p><h2>Por categoria</h2>{budgets.length === 0 ? <Empty>Crie seu primeiro limite mensal.</Empty> : budgets.map((budget) => <div className="budget-card" key={budget.id}><div><strong>{budget.categoryName}</strong><span>{brl(budget.spentMinor)} de {brl(budget.limitMinor)}</span></div><strong>{budget.percentage}%</strong><Progress value={budget.percentage} color={budget.percentage >= 100 ? "#ef5b5b" : budget.percentage >= 80 ? "#f6c945" : "#c7f34a"} /></div>)}</section><section className="panel panel--form"><p className="eyebrow">NOVO LIMITE</p><h2>Planeje antes de gastar</h2><form onSubmit={(event) => void submit(event)}><label>Categoria<select value={categoryId} onChange={(event) => setCategoryId(event.target.value)}>{categories.map((category) => <option key={category.id} value={category.id}>{category.name}</option>)}</select></label><label>Limite no mês<input inputMode="decimal" placeholder="800,00" value={limit} onChange={(event) => setLimit(event.target.value)} /></label><Button type="submit">Salvar orçamento</Button></form></section></div>;
}

function Structure({ reference, onSaved }: { reference: ReferenceData; onSaved(): Promise<void> }): ReactNode {
  const [mode, setMode] = useState<"account" | "card">("account");
  const [name, setName] = useState("");
  const [opening, setOpening] = useState("0");
  const [closingDay, setClosingDay] = useState("7");
  const [dueDay, setDueDay] = useState("15");
  const [limit, setLimit] = useState("");
  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (!name.trim()) return;
    if (mode === "account") await api("/accounts", { method: "POST", body: JSON.stringify({ name, type: "checking", openingBalanceMinor: moneyInputToMinor(opening) }) });
    else await api("/cards", { method: "POST", body: JSON.stringify({ name, closingDay: Number(closingDay), dueDay: Number(dueDay), creditLimitMinor: limit ? moneyInputToMinor(limit) : null, paymentAccountId: reference.accounts[0]?.id ?? null }) });
    setName("");
    await onSaved();
  };
  return <div className="split-layout"><section className="panel"><p className="eyebrow">ESTRUTURA</p><h2>Suas contas</h2>{reference.accounts.length === 0 ? <Empty>Nenhuma conta cadastrada.</Empty> : reference.accounts.map((account) => <div className="structure-row" key={account.id}><span><strong>{account.name}</strong><small>Saldo inicial {brl(account.openingBalanceMinor)}</small></span><span className="badge">Conta</span></div>)}<h2 className="section-gap">Cartões</h2>{reference.cards.length === 0 ? <Empty>Nenhum cartão cadastrado.</Empty> : reference.cards.map((card) => <div className="structure-row" key={card.id}><span><strong>{card.name}</strong><small>Fecha dia {card.closingDay} · vence dia {card.dueDay}</small></span><span className="badge">Cartão</span></div>)}</section><section className="panel panel--form"><div className="segmented"><button className={mode === "account" ? "active" : ""} onClick={() => setMode("account")}>Conta</button><button className={mode === "card" ? "active" : ""} onClick={() => setMode("card")}>Cartão</button></div><h2>{mode === "account" ? "Adicionar conta" : "Adicionar cartão"}</h2><form onSubmit={(event) => void submit(event)}><label>Nome<input value={name} onChange={(event) => setName(event.target.value)} placeholder={mode === "account" ? "Conta Nubank" : "Nubank Mastercard"} /></label>{mode === "account" ? <label>Saldo inicial<input inputMode="decimal" value={opening} onChange={(event) => setOpening(event.target.value)} /></label> : <><div className="form-row"><label>Fecha dia<input type="number" min="1" max="28" value={closingDay} onChange={(event) => setClosingDay(event.target.value)} /></label><label>Vence dia<input type="number" min="1" max="28" value={dueDay} onChange={(event) => setDueDay(event.target.value)} /></label></div><label>Limite do cartão<input inputMode="decimal" value={limit} onChange={(event) => setLimit(event.target.value)} placeholder="opcional" /></label></>}<Button type="submit">Adicionar</Button></form></section></div>;
}

function ImportPage({ onCommitted }: { onCommitted(): Promise<void> }): ReactNode {
  const [fileName, setFileName] = useState("");
  const [parsed, setParsed] = useState<CsvParseResult | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [positiveExpense, setPositiveExpense] = useState(true);
  const [working, setWorking] = useState(false);
  const [source, setSource] = useState("");
  const selectFile = async (file: File): Promise<void> => {
    const text = await file.text();
    setSource(text);
    setFileName(file.name);
    const result = parseNubankCsv(text);
    setPositiveExpense(result.detectedPositiveAsExpense);
    setParsed(result);
    setPreview(null);
  };
  const reparse = (value: boolean): void => { setPositiveExpense(value); setParsed(parseNubankCsv(source, value)); setPreview(null); };
  const createPreview = async (): Promise<void> => {
    if (!parsed?.rows.length) return;
    setWorking(true);
    try { setPreview(await api<ImportPreview>("/imports/preview", { method: "POST", body: JSON.stringify({ fileName, rows: parsed.rows }) })); }
    finally { setWorking(false); }
  };
  const commit = async (): Promise<void> => {
    if (!preview) return;
    setWorking(true);
    try { await api("/imports/commit", { method: "POST", body: JSON.stringify({ importId: preview.importId, includeDuplicateRows: [] }) }); setPreview(null); setParsed(null); setFileName(""); await onCommitted(); }
    finally { setWorking(false); }
  };
  return <section className="panel panel--full import-panel"><p className="eyebrow">IMPORTAÇÃO LOCAL</p><h2>Traga seu histórico do Nubank</h2><p className="muted">O arquivo é interpretado no seu navegador. Somente as linhas estruturadas da prévia são enviadas.</p><label className="file-drop"><input type="file" accept=".csv,text/csv" onChange={(event) => { const file = event.target.files?.[0]; if (file) void selectFile(file); }} /><strong>{fileName || "Escolher CSV"}</strong><span>Arraste ou selecione o arquivo exportado</span></label>{parsed && <><label className="check-line"><input type="checkbox" checked={positiveExpense} onChange={(event) => reparse(event.target.checked)} /> Neste arquivo, valores positivos representam gastos</label><div className="import-summary"><span><strong>{parsed.rows.length}</strong> linhas válidas</span><span><strong>{parsed.errors.length}</strong> com erro</span></div>{parsed.errors.length > 0 && <p className="warning">As linhas inválidas serão ignoradas. Primeira ocorrência: linha {parsed.errors[0]!.rowNumber} — {parsed.errors[0]!.message}</p>}<Button disabled={!parsed.rows.length || working} onClick={() => void createPreview()}>{working ? "Analisando…" : "Verificar duplicidades"}</Button></>}{preview && <div className="preview-box"><h3>Prévia pronta</h3><p>{preview.validRows} válidas · {preview.duplicateRows} possíveis duplicatas · {preview.invalidRows} inválidas</p><p className="muted">Duplicatas serão ignoradas automaticamente.</p><Button disabled={working} onClick={() => void commit()}>{working ? "Importando…" : `Importar ${preview.validRows - preview.duplicateRows} linhas`}</Button></div>}</section>;
}

function Settings({ reference, onChanged, onLogout }: { reference: ReferenceData; onChanged(): Promise<void>; onLogout(): void }): ReactNode {
  const download = async (format: "json" | "csv"): Promise<void> => {
    const file = await api<{ fileName: string; mimeType: string; content: string }>("/exports", { method: "POST", body: JSON.stringify({ format }) });
    const url = URL.createObjectURL(new Blob([file.content], { type: file.mimeType }));
    const anchor = document.createElement("a"); anchor.href = url; anchor.download = file.fileName; anchor.click(); URL.revokeObjectURL(url);
  };
  const revoke = async (id: string): Promise<void> => { if (!window.confirm("Revogar este aparelho? Ele não poderá enviar novas notificações.")) return; await api(`/devices/${id}/revoke`, { method: "POST" }); await onChanged(); };
  return <div className="split-layout"><section className="panel"><p className="eyebrow">APARELHOS</p><h2>Capturadores conectados</h2>{reference.devices.length === 0 ? <Empty>Use /conectar no bot para vincular o Android.</Empty> : reference.devices.map((device) => <div className="structure-row" key={device.id}><span><strong>{device.name}</strong><small>{device.lastSeenAt ? `Último contato ${new Date(device.lastSeenAt).toLocaleString("pt-BR")}` : "Ainda não sincronizou"}</small></span>{device.revoked ? <span className="badge badge--ignored">Revogado</span> : <Button variant="danger" onClick={() => void revoke(device.id)}>Revogar</Button>}</div>)}</section><section className="panel panel--form"><p className="eyebrow">PORTABILIDADE</p><h2>Backup dos seus dados</h2><p className="muted">Guarde uma cópia fora da Cloudflare pelo menos uma vez por mês.</p><Button onClick={() => void download("json")}>Baixar backup completo</Button><Button variant="quiet" onClick={() => void download("csv")}>Baixar lançamentos em CSV</Button><hr /><Button variant="quiet" onClick={onLogout}>Sair deste navegador</Button></section></div>;
}

function TransactionEditor({ transaction, reference, onClose, onSaved }: { transaction: Transaction; reference: ReferenceData; onClose(): void; onSaved(): Promise<void> }): ReactNode {
  const [status, setStatus] = useState<TransactionStatus>(transaction.status);
  const [kind, setKind] = useState<TransactionKind>(transaction.kind);
  const [description, setDescription] = useState(transaction.description ?? "");
  const [merchant, setMerchant] = useState(transaction.merchant ?? "");
  const [date, setDate] = useState(transaction.economicDate.slice(0, 10));
  const [categoryId, setCategoryId] = useState(transaction.categoryId ?? "");
  const [accountId, setAccountId] = useState(transaction.accountId ?? "");
  const [cardId, setCardId] = useState(transaction.cardId ?? "");
  const [installments, setInstallments] = useState(String(transaction.installmentCount));
  const [saving, setSaving] = useState(false);
  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault(); setSaving(true);
    try { await api(`/transactions/${transaction.id}`, { method: "PATCH", body: JSON.stringify({ status, kind, description, merchant, economicDate: date, categoryId: categoryId || null, accountId: accountId || null, cardId: cardId || null, installmentCount: Number(installments) }) }); await onSaved(); }
    finally { setSaving(false); }
  };
  return <div className="modal-backdrop" onMouseDown={onClose}><div className="modal" onMouseDown={(event) => event.stopPropagation()}><div className="modal__header"><div><p className="eyebrow">EDITAR LANÇAMENTO</p><h2>{brl(transaction.amountMinor)}</h2></div><button className="close-button" onClick={onClose}>×</button></div><form onSubmit={(event) => void submit(event)}><div className="form-row"><label>Estado<select value={status} onChange={(event) => setStatus(event.target.value as TransactionStatus)}><option value="pending">Pendente</option><option value="confirmed">Confirmado</option><option value="ignored">Ignorado</option><option value="deleted">Excluído</option></select></label><label>Tipo<select value={kind} onChange={(event) => setKind(event.target.value as TransactionKind)}><option value="expense">Gasto</option><option value="income">Entrada</option><option value="transfer">Transferência</option><option value="refund">Estorno</option></select></label></div><label>Estabelecimento<input value={merchant} onChange={(event) => setMerchant(event.target.value)} /></label><label>Descrição<input value={description} onChange={(event) => setDescription(event.target.value)} /></label><div className="form-row"><label>Data<input type="date" value={date} onChange={(event) => setDate(event.target.value)} /></label><label>Categoria<select value={categoryId} onChange={(event) => setCategoryId(event.target.value)}><option value="">Sem categoria</option>{reference.categories.map((category) => <option key={category.id} value={category.id}>{category.name}</option>)}</select></label></div><div className="form-row"><label>Conta<select value={accountId} onChange={(event) => setAccountId(event.target.value)}><option value="">Nenhuma</option>{reference.accounts.map((account) => <option key={account.id} value={account.id}>{account.name}</option>)}</select></label><label>Cartão<select value={cardId} onChange={(event) => setCardId(event.target.value)}><option value="">Nenhum</option>{reference.cards.map((card) => <option key={card.id} value={card.id}>{card.name}</option>)}</select></label></div><label>Parcelas<input type="number" min="1" max="60" value={installments} onChange={(event) => setInstallments(event.target.value)} /></label><div className="modal__actions"><Button type="button" variant="quiet" onClick={onClose}>Cancelar</Button><Button type="submit" disabled={saving}>{saving ? "Salvando…" : "Salvar"}</Button></div></form></div></div>;
}
